// The quick tour: a card in a corner that walks through the app page by page. Each step shows its page
// behind the card and rings the element it talks about; the pages stay usable meanwhile.
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { TOUR, TOUR_STORAGE_KEY } from '../lib/tour.ts';
import { useApp } from '../state.tsx';
import { useEscape } from './ui.tsx';

function markSeen(): void {
  try {
    localStorage.setItem(TOUR_STORAGE_KEY, 'seen');
  } catch {
    // storage blocked: the tour may open again on the next visit
  }
}

export function Tour() {
  const { tour, openTour, route } = useApp();

  // first visit of the trade page: the tour opens by itself, once the page has drawn. A visitor who lands
  // elsewhere by link came for that page; the ? in the header stays.
  useEffect(() => {
    let seen = true;
    try {
      seen = localStorage.getItem(TOUR_STORAGE_KEY) === 'seen';
    } catch {
      // storage blocked: never open uninvited
    }
    if (seen || route.page !== 'trade') return;
    const t = setTimeout(() => openTour(true), 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tour) markSeen();
  }, [tour]);

  if (!tour) return null;
  return <TourCard />;
}

function TourCard() {
  const { openTour, go, route, wallet, openFunds, openConnect } = useApp();
  const [i, setI] = useState(0);
  const step = TOUR[i]!;
  const last = i === TOUR.length - 1;
  // where the visitor was when the tour opened (a docs page, say): leaving the tour early brings them back
  const [origin] = useState(route);
  const close = useCallback(() => {
    openTour(false);
    go(origin, { replace: true });
  }, [openTour, go, origin]);
  // Escape closes a window opened from the tour (the deposit one) before it closes the tour
  useEscape(
    useCallback(() => {
      if (!document.querySelector('.scrim')) close();
    }, [close]),
  );

  // the page behind the card follows the step, without piling up history entries
  useEffect(() => {
    go({ page: step.page, doc: undefined }, { replace: true });
  }, [step.page, go]);

  // a ring on the element the step talks about: an attribute, not a class, because React rewrites the
  // className of an element it re-renders (a nav link turns `is-on` when its page opens) and would drop it
  useEffect(() => {
    if (!step.target) return;
    const els = [...document.querySelectorAll<HTMLElement>(`[data-tour="${step.target}"]`)];
    for (const el of els) el.setAttribute('data-tour-on', '');
    return () => {
      for (const el of els) el.removeAttribute('data-tour-on');
    };
  }, [step.target]);

  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'ArrowRight' && !last) setI((x) => x + 1);
      if (e.key === 'ArrowLeft' && i > 0) setI((x) => x - 1);
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, [i, last]);

  function finish() {
    openTour(false);
    go({ page: 'trade' }, { replace: true });
  }

  return createPortal(
    <div className="tour" role="dialog" aria-label="Quick tour" aria-live="polite">
      <header className="tour-head">
        <span className="tour-eyebrow">
          Quick tour · {i + 1}/{TOUR.length}
        </span>
        <button className="x" onClick={close} aria-label="Close the tour">
          ×
        </button>
      </header>
      <h3>{step.title}</h3>
      <p>{step.body}</p>
      {(step.docs || step.action) && (
        <div className="tour-links">
          {step.action === 'deposit' && (
            <button className="link" onClick={() => (wallet ? openFunds('deposit') : openConnect(true))}>
              {wallet ? 'Open the deposit window' : 'Connect a wallet'}
            </button>
          )}
          {step.docs && (
            <a href={`#/docs/${step.docs}`} onClick={() => go({ page: 'docs', doc: step.docs })}>
              Read more
            </a>
          )}
        </div>
      )}
      <footer className="tour-foot">
        <span className="tour-dots" aria-hidden>
          {TOUR.map((s, k) => (
            <i key={s.id} className={k === i ? 'is-on' : k < i ? 'is-done' : ''} />
          ))}
        </span>
        {i > 0 && (
          <button className="btn btn-ghost btn-sm" onClick={() => setI(i - 1)}>
            Back
          </button>
        )}
        {!last && (
          <button className="text-btn" onClick={close}>
            Skip
          </button>
        )}
        {last ? (
          <button className="btn btn-primary btn-sm" onClick={finish}>
            Start trading
          </button>
        ) : (
          <button className="btn btn-primary btn-sm" onClick={() => setI(i + 1)}>
            Next
          </button>
        )}
      </footer>
    </div>,
    document.body,
  );
}

/** A link that opens the tour, for the docs */
export function TourLink({ children }: { children: ReactNode }) {
  const { openTour } = useApp();
  return (
    <button className="link tour-link" onClick={() => openTour(true)}>
      {children}
    </button>
  );
}
